import React, { useState, useEffect } from 'react';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { getScheduledInvoicePreview, createScheduledInvoice } from '../../api/spotlights';
import { fmtMoney2 } from '../../helpers/spotlights_dashboard_helpers';

// Generate the brand's next scheduled invoice without waiting for the nightly run — the recovery
// path when the nightly held it on a ledger/ad-platform mismatch. Everything (period, line items,
// the reconciliation gate) is computed server-side by the same code the nightly uses; overriding
// the gate is for when the difference is handled another way (e.g. credited) and requires a reason.
const REPAIR_SCRIPTS = { meta: 'repair_spotlight_analytics.js', tiktok: 'repair_tiktok_analytics.js' };

const ScheduledInvoiceModal = ({ brand, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [overrideReconciliation, setOverrideReconciliation] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = () => {
    setPreview(null);
    setPreviewError(null);
    getScheduledInvoicePreview(brand.Brand_id)
      .then(setPreview)
      .catch(e => setPreviewError(typeof e === 'string' ? e : 'Failed to load the open billing period.'));
  };
  useEffect(loadPreview, [brand.Brand_id]);

  // One verdict per platform the brand's billing touches ({ platform, label, ledgerSpend,
  // platformSpend, verifiable, reason, holds }); the top-level flags summarize them.
  const reconciliation = preview?.reconciliation;
  const platforms = reconciliation?.platforms || [];
  const holdingPlatforms = platforms.filter(v => v.holds);
  const verifiedPlatforms = platforms.filter(v => v.verifiable);
  const outOfWindowLabels = [...new Set(platforms.filter(v => v.reason === 'out_of_window' && v.ledgerSpend > 0).map(v => v.label))];
  const unavailableLabels = [reconciliation?.metaUnavailable && 'Meta', reconciliation?.tiktokUnavailable && 'TikTok'].filter(Boolean);
  const needsOverride = !!(
    reconciliation?.wouldHold ||
    reconciliation?.metaUnavailable ||
    reconciliation?.tiktokUnavailable ||
    reconciliation?.outOfWindow
  );
  const gateLabels = [...new Set([...holdingPlatforms.map(v => v.label), ...unavailableLabels, ...outOfWindowLabels])];
  const overrideReady = overrideReconciliation && overrideReason.trim().length > 0;
  const canSend = !!preview && preview.canCreate && !sending && (!needsOverride || overrideReady);

  const collection = preview?.collection;
  const collectionLabel = !collection
    ? null
    : !collection.eligible
    ? collection.reason
    : collection.method === 'charge_automatically'
    ? 'Will auto-charge the card on file.'
    : collection.netDays > 0
    ? `Will send a Stripe invoice due in ${collection.netDays} day(s).`
    : 'Will send a Stripe invoice due immediately.';

  const previewInvoices = preview?.invoices || [];
  const perGroup = preview?.grouping === 'per_group';
  const invoiceCount = previewInvoices.length;

  const send = async () => {
    const confirmed = window.confirm(
      `Generate ${invoiceCount > 1 ? `${invoiceCount} scheduled invoices` : 'the scheduled invoice'} for ${brand.brandName}: ${fmtMoney2(
        preview.amount
      )} for ${preview.periodStart} – ${preview.periodEnd}?` +
        (needsOverride ? `\n\nThis OVERRIDES the ${gateLabels.join('/') || 'Meta'} reconciliation gate.` : '')
    );
    if (!confirmed) return;
    setSending(true);
    try {
      const r = await createScheduledInvoice(brand.Brand_id, {
        periodStart: preview.periodStart,
        periodEnd: preview.periodEnd,
        // The server refuses if credits or spend moved this total since the preview.
        expectedAmount: preview.amount,
        overrideReconciliation: needsOverride && overrideReconciliation,
        overrideReason: needsOverride ? overrideReason.trim() : undefined
      });
      const invoices = r.invoices || (r.invoice ? [r.invoice] : []);
      setResult(invoices);
      cogoToast.success(`Scheduled invoice${invoices.length > 1 ? 's' : ''} generated for ${brand.brandName}.`);
      onDone && onDone();
    } catch (e) {
      cogoToast.error(typeof e === 'string' ? e : 'Failed to create the invoice.');
      // Any create failure means the preview no longer reflects what the server sees (a fresh
      // hold, a rolled-over period, invoices that DID send before a mid-run failure) — reload the
      // preview and the parent's invoice/credit tables so the retry quotes current numbers.
      loadPreview();
      onDone && onDone();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='sl-modal-overlay' onMouseDown={onClose}>
      <div className='sl-modal' style={{ maxWidth: 680 }} onMouseDown={e => e.stopPropagation()}>
        <div className='sl-modal-head'>
          <div className='sl-modal-title'>Generate scheduled invoice — {brand.brandName}</div>
          <button className='sl-modal-close' onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className='sl-modal-body'>
          {result ? (
            <div>
              {result.map(invoice => (
                <div key={invoice.id} className='sl-card-sub' style={{ marginBottom: 12 }}>
                  Invoice #{invoice.id}
                  {invoice.groupName ? ` (set ${invoice.groupName})` : ''} created for <strong>{fmtMoney2(invoice.amount)}</strong> (
                  {fmtMoney2(invoice.spendAmount)} + {fmtMoney2(invoice.platformFeeAmount)} fee
                  {invoice.creditAmount > 0 ? ` - ${fmtMoney2(invoice.creditAmount)} credit` : ''}).{' '}
                  {invoice.stripeHostedUrl && (
                    <a href={invoice.stripeHostedUrl} target='_blank' rel='noreferrer'>
                      Hosted invoice ↗
                    </a>
                  )}{' '}
                  {invoice.stripeDashboardUrl && (
                    <a href={invoice.stripeDashboardUrl} target='_blank' rel='noreferrer'>
                      Stripe dashboard ↗
                    </a>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className='sl-btn small' onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          ) : previewError ? (
            <div className='sl-card-sub' style={{ color: '#c0392b' }}>
              {previewError}
            </div>
          ) : !preview ? (
            <div className='sl-card-sub'>Loading the open billing period…</div>
          ) : (
            <>
              <div className='sl-card-sub'>
                Open {preview.billingCadence} period: <strong>{preview.periodStart}</strong> – <strong>{preview.periodEnd}</strong>. The nightly
                run would bill this same period; generating it now advances the brand's billing to the next period.
              </div>

              {collection && (
                <div className='sl-card-sub' style={{ marginTop: 10, color: collection.eligible ? undefined : '#c0392b' }}>
                  {collectionLabel}
                </div>
              )}

              {reconciliation &&
                (reconciliation.wouldHold ? (
                  holdingPlatforms.map(v => (
                    <div key={v.platform} className='sl-card-sub' style={{ marginTop: 10, color: '#c0392b' }}>
                      ⚠ Ledger/{v.label} mismatch: the ledger says <strong>{fmtMoney2(v.ledgerSpend)}</strong> but {v.label} reports{' '}
                      <strong>{fmtMoney2(v.platformSpend)}</strong> for this period. The nightly run would hold this invoice. Repair the ledger
                      first (
                      <code>
                        {REPAIR_SCRIPTS[v.platform]} -b {brand.Brand_id} --execute
                      </code>
                      ), or override below if the difference is handled another way.
                    </div>
                  ))
                ) : unavailableLabels.length ? (
                  <div className='sl-card-sub' style={{ marginTop: 10, color: '#c0392b' }}>
                    ⚠ Could not reach {unavailableLabels.join(' or ')} to verify this period's spend. Retry later, or override below to bill
                    unverified.
                  </div>
                ) : reconciliation.outOfWindow ? (
                  <div className='sl-card-sub' style={{ marginTop: 10, color: '#c0392b' }}>
                    ⚠ This period extends beyond the {outOfWindowLabels.join('/') || 'Meta'} verification window, so its spend can't be verified
                    automatically. Verify it manually, then override below to bill.
                  </div>
                ) : reconciliation.verifiable ? (
                  verifiedPlatforms.map(v => (
                    <div key={v.platform} className='sl-card-sub' style={{ marginTop: 10 }}>
                      ✓ Ledger matches {v.label} ({fmtMoney2(v.ledgerSpend)} vs {fmtMoney2(v.platformSpend)}).
                    </div>
                  ))
                ) : (
                  <div className='sl-card-sub' style={{ marginTop: 10 }}>
                    Could not verify against Meta (no campaign on file, or the period is outside the comparison window). Billing proceeds unverified.
                  </div>
                ))}

              {previewInvoices.map(invoice => (
                <div key={invoice.SpotlightGroup_id || 'combined'}>
                  {perGroup && (
                    <div className='sl-card-sub' style={{ marginTop: 14 }}>
                      <strong>{invoice.groupName}</strong> — {invoice.periodStart} – {invoice.periodEnd}: {fmtMoney2(invoice.spendAmount)} +{' '}
                      {fmtMoney2(invoice.platformFeeAmount)} fee
                      {invoice.creditAmount > 0 ? ` - ${fmtMoney2(invoice.creditAmount)} credit` : ''} = <strong>{fmtMoney2(invoice.amount)}</strong>
                    </div>
                  )}
                  <table className='sl-table' style={{ marginTop: perGroup ? 6 : 14 }}>
                    <thead>
                      <tr>
                        <th className='no-sort'>Spotlight</th>
                        {!perGroup && <th className='no-sort'>Set</th>}
                        <th className='no-sort num'>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.lineItems.map(li => (
                        <tr key={`${li.SpotlightGroup_id || 0}-${li.Spotlight_id}`}>
                          <td>{li.spotlightName}</td>
                          {!perGroup && <td>{li.groupName || '—'}</td>}
                          <td className='num'>{fmtMoney2(li.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {previewInvoices.length === 0 && (
                <div className='sl-card-sub' style={{ marginTop: 14 }}>
                  No spend recorded in the open period.
                </div>
              )}

              {preview.pendingCredits?.length > 0 && (
                <div className='sl-card-sub' style={{ marginTop: 10 }}>
                  Pending credits:{' '}
                  {preview.pendingCredits.map(credit => (
                    <span key={credit.id} style={{ marginRight: 8 }}>
                      {credit.description} ({fmtMoney2(credit.remaining)} remaining)
                    </span>
                  ))}
                  {preview.creditAmount > 0
                    ? ` — ${fmtMoney2(preview.creditAmount)} will be applied to this run.`
                    : ' — nothing to apply them to in this run.'}
                </div>
              )}

              {needsOverride && (
                <div style={{ marginTop: 14 }}>
                  <label style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type='checkbox' checked={overrideReconciliation} onChange={e => setOverrideReconciliation(e.target.checked)} />
                    Bill anyway — override the reconciliation gate
                  </label>
                  {overrideReconciliation && (
                    <input
                      type='text'
                      placeholder='Required: why is billing the ledger amount correct? e.g. crediting the missed spend separately'
                      value={overrideReason}
                      maxLength={300}
                      onChange={e => setOverrideReason(e.target.value)}
                      style={{ padding: 8, borderRadius: 8, width: '100%', marginTop: 8 }}
                    />
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13 }}>
                  {fmtMoney2(preview.spendAmount)} + {fmtMoney2(preview.platformFeeAmount)} fee ({preview.platformFeePercentage}%)
                  {preview.creditAmount > 0 ? ` - ${fmtMoney2(preview.creditAmount)} credit` : ''} = <strong>{fmtMoney2(preview.amount)}</strong>
                </span>
                <button className='sl-btn' disabled={!canSend} onClick={send}>
                  {sending
                    ? 'Generating…'
                    : needsOverride
                    ? 'Generate (override)'
                    : invoiceCount > 1
                    ? `Generate ${invoiceCount} invoices`
                    : 'Generate invoice'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduledInvoiceModal;
